/**
 * ч.1447 (рішення власника): «створити ПН на аванс» у надходженні — одразу податкова накладна,
 * як у 1С: підстава — саме надходження, рядки — з рахунку, якщо його вказано. ПДВ з авансу
 * нараховує надходження, проведення накладної переносить його на 6412.
 *
 * «Реєстрація авансу» лишилась для надходжень, проведених без ПДВ з авансу (до зміни або без
 * «Суми ПДВ»): дія веде туди. Що саме показати — каже сервер (`advance-from-receipt`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { taxInvoicesOutgoing, type AdvanceFromReceiptState, type ReceiptType } from '@/api/taxInvoices';

export interface RailAction { label: string; title?: string; disabled?: boolean; onClick: () => void }

const fmt = (n: number) => n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dmy = (d: string | null) => (d ? new Date(d).toLocaleDateString('uk-UA') : '');

export const advanceStateKey = (receiptType: ReceiptType, receiptId: string | undefined) =>
  ['adv-pn-state', receiptType, receiptId] as const;

/** Перехід за станом: відкрити наявне або створити. Спільне для редактора і списку. */
export async function runAdvanceAction(
  st: AdvanceFromReceiptState,
  opts: { organizationId: string; receiptType: ReceiptType; receiptId: string;
    navigate: (to: string) => void; create: () => Promise<{ id: string }> },
): Promise<void> {
  const source = opts.receiptType === 'doc.bank_receipts' ? 'bank_receipt' : 'cash_receipt';
  if (st.mode === 'invoice' && st.invoice_id) opts.navigate(`/tax-invoices-outgoing/${st.invoice_id}`);
  else if (st.mode === 'registration' && st.registration_id) {
    opts.navigate(`/taxation/advance-payment-registrations/${st.registration_id}`);
  } else if (st.mode === 'create_registration') {
    opts.navigate(`/taxation/advance-payment-registrations/new?source_type=${source}&source_id=${opts.receiptId}`);
  } else if (st.mode === 'create') {
    const inv = await opts.create();
    opts.navigate(`/tax-invoices-outgoing/${inv.id}`);
  }
}

export function useAdvanceInvoiceAction(p: {
  receiptType: ReceiptType;
  receiptId: string | undefined;
  organizationId: string;
  /** Дія взагалі доречна (надходження від покупця). */
  applicable: boolean;
  isNew: boolean;
  /** Причина, відома на сторінці (організація не платник ПДВ). */
  blockedReason?: string | null;
  /** Час останнього завантаження надходження: після «зберегти» / «провести» стан перечитується. */
  version?: number;
}): { action: RailAction | null; state: AdvanceFromReceiptState | undefined } {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const enabled = p.applicable && !p.isNew && !p.blockedReason && !!p.receiptId && !!p.organizationId;
  const stateQ = useQuery({
    queryKey: [...advanceStateKey(p.receiptType, p.receiptId), p.version ?? 0],
    queryFn: () => taxInvoicesOutgoing.advanceFromReceiptState(p.organizationId, p.receiptType, p.receiptId!),
    enabled,
  });
  const create = useMutation({
    mutationFn: () => taxInvoicesOutgoing.createAdvanceFromReceipt(p.organizationId, p.receiptType, p.receiptId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: advanceStateKey(p.receiptType, p.receiptId) });
      qc.invalidateQueries({ queryKey: ['tax-invoices-outgoing'] });
      qc.invalidateQueries({ queryKey: ['bank-receipts'] });
    },
  });
  if (!p.applicable) return { action: null, state: undefined };
  const st = stateQ.data;
  const label = st?.mode === 'invoice' ? 'відкрити ПН на аванс'
    : st?.mode === 'registration' ? 'відкрити реєстрацію авансу' : 'створити ПН на аванс';
  const title = p.blockedReason ? p.blockedReason
    : p.isNew ? 'Спершу збережіть документ'
    : !st ? (stateQ.isError ? 'Не вдалося перевірити, чи виник аванс' : 'Перевіряю, чи виник аванс…')
    : st.mode === 'invoice'
      ? `ПН на аванс № ${st.invoice_number ?? ''} від ${dmy(st.invoice_date)} — ${st.invoice_posted ? 'проведена' : 'чернетка'}`
    : st.mode === 'registration'
      ? 'За цим надходженням уже є реєстрація авансу — ПН складається з неї'
    : st.mode === 'create_registration'
      ? 'Надходження проведено без ПДВ з авансу — ПН складається через «Реєстрацію авансу», вона й нарахує ПДВ (або перепроведіть надходження)'
    : st.mode === 'create'
      ? `Податкова накладна на аванс ${fmt(st.advance_amount)} грн: підстава — це надходження, рядки — з рахунку, якщо його вказано. ПДВ з авансу нараховує надходження, проведення ПН переносить його на 6412`
    : (st.reason ?? 'Аванс не виникає');
  const disabled = !!p.blockedReason || p.isNew || !st || st.mode === 'none' || create.isPending;
  return {
    state: st,
    action: {
      label, title, disabled,
      onClick: () => {
        if (!st || !p.receiptId) return;
        void runAdvanceAction(st, {
          organizationId: p.organizationId, receiptType: p.receiptType, receiptId: p.receiptId,
          navigate, create: () => create.mutateAsync(),
        }).catch(() => undefined);  // текст відмови показує перехоплювач запитів
      },
    },
  };
}
